import React, { useState } from 'react';
import { toast } from 'sonner';
import { History, Sparkles, Loader2, BookMarked } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { pollJob } from '../../utils/aiJobs';
import { DateField } from '../DateField';
import { useLang } from '../../i18n/LangContext';
import { fmtDate } from '../../lib/dates';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const fmt = (iso) => (iso ? fmtDate(iso) : 'hoy');

const ArticleTimeline = ({ c, normId, factsDate }) => {
  const { api } = useAuth();
  const { t } = useLang();
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const [at, setAt] = useState(factsDate || '');
  React.useEffect(() => { setAt(factsDate || ''); }, [factsDate]);
  const load = () => api.get('/legislative/article-history', { params: { country: c.country, norm_id: normId, articulo: c.articulo } }).then((r) => setH(r.data)).catch(() => setH({ versions: [] }));
  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reconstruct = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/legislative/article-history/ai', { country: c.country, norm_id: normId, articulo: c.articulo });
      await pollJob(api, data.job_id);
      await load();
    } catch (e) { toast.error(e.message || 'No se pudo reconstruir'); } finally { setBusy(false); }
  };
  if (!h) return <Loader2 className="h-4 w-4 animate-spin text-blue-400" />;
  const versions = h.versions || [];
  const applies = at && [...versions].reverse().find((v) => v.valid_from.slice(0, 10) <= at);
  return (
    <div className="space-y-3" data-testid={`article-timeline-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
        ¿Qué versión regía a la fecha de los hechos?
        <DateField value={at} onChange={setAt} testid={`timeline-date-input-${c.id}`} className="py-0.5 text-xs" clearable />
        {at && <span data-testid={`timeline-applies-${c.id}`} className={applies ? 'text-emerald-300' : 'text-amber-300'}>{applies ? `${t('Versión oficial registrada desde')} ${fmt(applies.valid_from)}` : t('Anterior al inicio del seguimiento oficial: revisa la reconstrucción con IA')}</span>}
      </div>
      <ol className="relative ml-2 space-y-3 border-l border-slate-700 pl-4">
        {(h.ai?.reformas || []).map((r, i) => (
          <li key={`ai-${i}`} data-testid="timeline-ai-item" className="relative">
            <span className="absolute -left-[1.4rem] top-1 h-3 w-3 rounded-full bg-violet-500/70" />
            <p className="text-xs"><b className="text-violet-200">{r.fecha}</b> · {r.norma_modificatoria} <span className="ml-1 rounded-full bg-violet-500/20 px-1.5 text-[10px] text-violet-200">{t('IA · no oficial · certeza')} {r.certeza}</span></p>
            <p className="text-xs text-slate-400">{r.cambio}</p>
            {r.gazette && <a href={r.gazette.url} target="_blank" rel="noreferrer" data-testid="timeline-ai-gazette-link" className="text-[11px] text-violet-300 hover:underline">{t('Verificar en')} {r.gazette.name} ↗</a>}
          </li>
        ))}
        {versions.map((v) => (
          <li key={v.valid_from + v.n} data-testid="timeline-official-item" className={`relative ${applies === v ? "rounded bg-emerald-500/10 p-1" : ""}`}>
            <span className={`absolute -left-[1.4rem] top-1 h-3 w-3 rounded-full ${v.current ? 'bg-emerald-500' : 'bg-slate-500'}`} />
            <p className="text-xs"><b className="text-emerald-200">{v.baseline ? `${t('Inicio del seguimiento oficial')} · ${fmt(v.valid_from)}` : `${t('Reforma detectada')} · ${fmt(v.valid_from)}`}</b>{v.valid_to ? ` → ${fmt(v.valid_to)}` : ` → ${t('vigente')}`}
              <span className="ml-1 rounded-full bg-emerald-500/20 px-1.5 text-[10px] text-emerald-200">Oficial</span></p>
            {v.amending && (
              <div className="mt-1 rounded border border-emerald-700/40 bg-emerald-950/20 p-2 text-[11px]" data-testid="timeline-amending">
                <p className="font-semibold text-emerald-200">Norma modificatoria</p>
                {v.amending.candidates?.length ? v.amending.candidates.map((b) => (
                  <a key={b.boletin} href={b.link} target="_blank" rel="noreferrer" className="block text-slate-300 hover:underline">• {b.boletin}: {b.titulo?.slice(0, 110)} · {b.etapa} ↗</a>
                )) : <p className="text-slate-400">Sin coincidencias en los proyectos seguidos.</p>}
                <a href={v.amending.gazette.url} target="_blank" rel="noreferrer" data-testid="timeline-gazette-link" className="text-emerald-300 hover:underline">{t('Buscar en')} {v.amending.gazette.name} ↗</a>
              </div>
            )}
            <details className="text-xs text-slate-400"><summary className="cursor-pointer">Ver texto</summary><p className="mt-1 whitespace-pre-line font-serif text-slate-300">{v.text}</p></details>
          </li>
        ))}
      </ol>
      {h.ai ? <p className="text-[11px] text-violet-200/80">{h.ai.aviso} {h.ai.comentario}</p> : (
        <button onClick={reconstruct} disabled={busy} data-testid={`timeline-ai-btn-${c.id}`} className="flex items-center gap-1.5 rounded-full border border-violet-500/50 px-3 py-1 text-xs text-violet-200 hover:bg-violet-500/10 disabled:opacity-50">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} {t('Reconstruir reformas anteriores con IA')}
        </button>
      )}
    </div>
  );
};

export const CitationsPanel = ({ matter, onUpdated }) => {
  const { api } = useAuth();
  const { t } = useLang();
  const [facts, setFacts] = useState(matter.facts_date || '');
  const saveFacts = async (v) => {
    setFacts(v);
    try {
      await api.put(`/crm/matters/${matter.id}/facts-date`, { facts_date: v || null });
      toast.success(t(v ? 'Fecha de los hechos guardada: los escritos citarán la versión vigente a esa fecha' : 'Fecha de los hechos eliminada'));
      onUpdated && onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
  };
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const cites = matter.legal_citations || [];
  if (!cites.length) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`citations-btn-${matter.id}`} className="mt-2 flex items-center gap-1.5 text-xs text-blue-300 hover:underline">
        <BookMarked className="h-3.5 w-3.5" /> {t('Normas citadas')} ({cites.length}) · {t('historial de reformas')}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid={`citations-dialog-${matter.id}`}>
          <DialogHeader>
            <DialogTitle>{t('Normas citadas en')} «{matter.title}»</DialogTitle>
            <DialogDescription>Los escritos nuevos usan siempre el texto oficial vigente y agregan nota al pie si cambió.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 text-sm" data-testid={`facts-date-box-${matter.id}`}>
            <span className="font-semibold text-blue-200">Fecha de los hechos del caso:</span>
            <DateField value={facts} onChange={saveFacts} testid={`facts-date-input-${matter.id}`} clearable />
            <span className="text-xs text-slate-400">{t(facts ? 'Cada escrito nuevo citará la versión vigente a esta fecha.' : 'Sin fecha: se cita el texto vigente hoy.')}</span>
          </div>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {cites.map((c) => (
              <div key={c.id} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3" data-testid={`citation-item-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold">{c.articulo} · {c.norma}</p>
                  {c.norm_id && <button onClick={() => setSel(sel === c.id ? null : c.id)} data-testid={`citation-history-btn-${c.id}`} className="flex items-center gap-1 text-xs text-blue-300 hover:underline"><History className="h-3.5 w-3.5" /> Historial</button>}
                </div>
                <p className="mt-1 line-clamp-3 font-serif text-xs text-slate-400">{c.contenido}</p>
                {sel === c.id && <div className="mt-3 border-t border-slate-800 pt-3"><ArticleTimeline c={c} normId={c.norm_id} factsDate={facts} /></div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
